import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IITH } from '@/pages/landing/iithBrand';
import PatientInfoFields from './PatientInfoFields';
import SeekingHospitalFields from './SeekingHospitalFields';
import StationTypeFields from './StationTypeFields';
import PreferredDoctorField from './PreferredDoctorField';
import CreditLetterFields from './CreditLetterFields';
import { referralPortalApi } from '../api/referralPortal.api';

export default function ReferralRequestForm({ me, catalogue, workflow, onCreated }) {
  const selfPatient = me?.patient;
  const [scope, setScope] = useState('self');
  const [stationType, setStationType] = useState('in_station');
  const [preferredDoctorId, setPreferredDoctorId] = useState('');
  const [needsCreditLetter, setNeedsCreditLetter] = useState(null);
  const [saving, setSaving] = useState(false);
  const [patient, setPatient] = useState(() => selfSnapshot(selfPatient, me));
  const [seeking, setSeeking] = useState({
    hospital_clinic: '', specialisation: '', city: 'Hyderabad', reason: '',
  });

  const family = useMemo(() => me?.family || [], [me]);
  const pastNd = useMemo(() => me?.past_non_dependents || [], [me]);

  const onPickFamily = (id) => {
    const f = family.find((x) => x.id === id);
    if (!f) return;
    setPatient({
      patient_id: f.id, name: f.name || '', phone: f.phone || patient.phone,
      age: f.age ?? '', gender: f.gender || '', dob: '',
      relationship: f.relationship || 'other', mrn: f.mrn || '', address: f.address || '',
    });
  };

  const onPickNonDependent = (id) => {
    const n = pastNd.find((x) => x.id === id);
    if (!n) return;
    setPatient({
      patient_id: '', name: n.name || '', phone: n.phone || '',
      age: n.age ?? '', gender: n.gender || '', dob: n.dob || '',
      relationship: n.relationship || 'guest', mrn: n.mrn || '', address: n.address || '',
    });
  };

  const onCreateNonDependent = () => {
    setPatient({
      patient_id: '', name: '', phone: selfPatient?.phone || patient.phone || '',
      age: '', gender: '', dob: '', relationship: 'guest', mrn: '', address: '',
    });
  };

  const onScope = (next) => {
    setScope(next);
    if (next === 'self') setPatient(selfSnapshot(selfPatient, me));
    else if (next === 'dependent') {
      setPatient((p) => ({ ...p, relationship: 'spouse', patient_id: '', name: '' }));
    } else {
      onCreateNonDependent();
    }
  };

  const onStationType = (next) => {
    setStationType(next);
    setSeeking((s) => ({
      ...s,
      city: next === 'in_station' ? 'Hyderabad' : '',
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (stationType === 'out_station' && !(seeking.city || '').trim()) {
      toast.error('Enter the city for an out-station referral');
      return;
    }
    if (needsCreditLetter !== true && needsCreditLetter !== false) {
      toast.error('Please indicate if you need a credit letter');
      return;
    }
    setSaving(true);
    try {
      const created = await referralPortalApi.createRequest({
        station_type: stationType,
        preferred_doctor_id: preferredDoctorId,
        needs_credit_letter: needsCreditLetter,
        patient_scope: scope,
        patient: { ...patient, age: patient.age === '' ? null : Number(patient.age) },
        seeking: {
          ...seeking,
          city: stationType === 'in_station' ? 'Hyderabad' : seeking.city,
        },
      });
      toast.success(
        stationType === 'out_station'
          ? 'Request submitted — awaiting FIC approval'
          : 'Referral request submitted',
      );
      onCreated?.(created);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-8">
      <div className="rounded-3xl border border-stone-200/80 bg-white/90 p-5 shadow-sm sm:p-8">
        <PatientInfoFields
          form={patient}
          setForm={setPatient}
          relationships={catalogue?.dependent_relationships || catalogue?.relationships}
          affiliations={catalogue?.non_dependent_affiliations}
          family={family}
          pastNonDependents={pastNd}
          onPickFamily={onPickFamily}
          onPickNonDependent={onPickNonDependent}
          onCreateNonDependent={onCreateNonDependent}
          scope={scope}
          setScope={onScope}
        />
      </div>
      <div className="rounded-3xl border border-stone-200/80 bg-white/90 p-5 shadow-sm sm:p-8">
        <StationTypeFields
          stationType={stationType}
          setStationType={onStationType}
          workflow={workflow}
        />
      </div>
      <div className="rounded-3xl border border-stone-200/80 bg-white/90 p-5 shadow-sm sm:p-8">
        <SeekingHospitalFields
          form={seeking}
          setForm={setSeeking}
          specialisations={catalogue?.items}
          stationType={stationType}
        />
      </div>
      <div className="rounded-3xl border border-stone-200/80 bg-white/90 p-5 shadow-sm sm:p-8">
        <PreferredDoctorField value={preferredDoctorId} onChange={setPreferredDoctorId} />
      </div>
      <div className="rounded-3xl border border-stone-200/80 bg-white/90 p-5 shadow-sm sm:p-8">
        <CreditLetterFields
          needsCreditLetter={needsCreditLetter}
          setNeedsCreditLetter={setNeedsCreditLetter}
        />
      </div>
      <div className="flex justify-end pt-2">
        <Button
          type="submit"
          disabled={saving}
          className="h-11 rounded-full px-6 font-semibold text-white shadow-md hover:opacity-95 disabled:opacity-70"
          style={{ backgroundColor: IITH.saffron }}
        >
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
          Submit referral request
        </Button>
      </div>
    </form>
  );
}

function selfSnapshot(selfPatient, me) {
  return {
    patient_id: selfPatient?.id || '',
    name: selfPatient?.name || me?.user?.name || '',
    phone: selfPatient?.phone || '',
    age: selfPatient?.age ?? '',
    gender: selfPatient?.gender || '',
    dob: '',
    relationship: 'self',
    mrn: selfPatient?.mrn || '',
    address: selfPatient?.address || '',
  };
}
